import ColumnsButton from "../common/ColumnsButton";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AppContext from "../../context/AppContext";
import { deleteinventory, searchMeterInventory, getMeterInventoryColumnValues } from "../../service/Inventory";
import { Spinner } from "../../Spinner";
import { UploadExcelReport } from "./UploadExcelReport";
import { UpdateStock } from "./UpdateStock";
import { DownloadReport } from "./DownloadReport";
import translations from "../common/Translations";
import DeletePermission from "../suspence/DeletePermission";
import { toast } from "../../utils/toast";
import CommonButton from "../common/CommonButton";
import { Pencil, Search, Trash2, RotateCcw, Columns3, Rows3, LayoutGrid, GripVertical, Filter, Calendar, ChevronDown, Download, SearchX, Inbox } from "lucide-react";
import datePickerPopper from "../common/DatePickerPopper";
import { getPermissionByScreenCode } from "../../utils/permissions";
import useDirection from "../../hooks/useDirection";
import PageSizeSelector from "../common/PageSizeSelector";
import ActionIconButton from "../common/ActionIconButton";
import PagerArrowButton from "../common/PagerArrowButton";
import ColumnsCustomizeModal from "../common/ColumnsCustomizeModal";
import SearchFieldPicker from "../common/SearchFieldPicker";
import HoverTooltip from "../common/HoverTooltip";
import TableScrollShell from "../common/TableScrollShell";

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const formatShortDate = (ymd) => {
  const d = parseYmd(ymd);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const STOCK_TABLE_VIEW_KEY = "stockManageTableView";
const LEGACY_DEFAULT_VISIBLE_STOCK_COLUMNS = [
  "components",
  "meterType",
  "manufacturingYear",
  "meterMake",
  "activationDate",
  "installationStatus",
];
const STOCK_COLUMN_PREFS_KEY = "stockManageColumnPrefs";
const DEFAULT_COLUMN_SELECTION_MODE = "custom";
const DEFAULT_COLUMN_ORDER_STOCK = [
  "projectId",
  "components",
  "meterType",
  "manufacturingYear",
  "meterMake",
  "activationDate",
  "batchNo",
  "installationStatus",
  "createdBy",
  "createdAt",
  "simNo",
  "ipAddress",
  "iccid",
  "apn",
  "op",
  "balance",
  "status",
  "simDetailUpdatedAt",
  "transferredFrom",
  "transferredTo",
];

const readStockTableView = () => {
  try {
    const raw = localStorage.getItem(STOCK_TABLE_VIEW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

// Same shape and rules as TaskManageMain's column prefs.
const ALL_COLUMNS_PREFS = { visibleColumnKeys: [], knownHeaderKeys: [], selectionMode: "all" };

const readStockColumnPrefs = () => {
  const parseSafe = (raw) => {
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  };
  let prefs = null;
  try {
    prefs =
      parseSafe(sessionStorage.getItem(STOCK_COLUMN_PREFS_KEY)) ||
      parseSafe(localStorage.getItem(STOCK_COLUMN_PREFS_KEY));
  } catch {
    prefs = null;
  }
  if (prefs) {
    return {
      visibleColumnKeys: Array.isArray(prefs.visibleColumnKeys) ? prefs.visibleColumnKeys : [],
      knownHeaderKeys: Array.isArray(prefs.knownHeaderKeys) ? prefs.knownHeaderKeys : [],
      selectionMode: prefs.selectionMode === "all" ? "all" : DEFAULT_COLUMN_SELECTION_MODE,
    };
  }
  const legacyKeys = readStockTableView()?.visibleColumnKeys;
  const isLegacyCustom =
    Array.isArray(legacyKeys) &&
    !(
      legacyKeys.length === LEGACY_DEFAULT_VISIBLE_STOCK_COLUMNS.length &&
      LEGACY_DEFAULT_VISIBLE_STOCK_COLUMNS.every((key) => legacyKeys.includes(key))
    );
  if (!isLegacyCustom) return ALL_COLUMNS_PREFS;
  const migrated = {
    visibleColumnKeys: legacyKeys,
    knownHeaderKeys: DEFAULT_COLUMN_ORDER_STOCK,
    selectionMode: DEFAULT_COLUMN_SELECTION_MODE,
  };
  persistStockColumnPrefs(migrated);
  return migrated;
};

const persistStockColumnPrefs = (prefs) => {
  try {
    const payload = JSON.stringify(prefs);
    sessionStorage.setItem(STOCK_COLUMN_PREFS_KEY, payload);
    localStorage.setItem(STOCK_COLUMN_PREFS_KEY, payload);
  } catch {
    // ignore storage errors (e.g. private browsing / quota)
  }
};

// Applied filters (search, date range, funnel column filters) persist across
// navigation/refresh the same way TaskListView.js persists its table filters:
// sessionStorage as the primary store with a localStorage backup, read back
// on mount, and explicitly cleared only when the user hits Reset -- filters
// are never silently dropped just because the user navigated away and back.
const STOCK_MANAGE_TABLE_FILTERS_KEY = "stockManageTableFilters";

const readStockManageFilters = () => {
  try {
    const raw = sessionStorage.getItem(STOCK_MANAGE_TABLE_FILTERS_KEY);
    if (raw) return JSON.parse(raw);
    const rawLocal = localStorage.getItem(STOCK_MANAGE_TABLE_FILTERS_KEY);
    return rawLocal ? JSON.parse(rawLocal) : null;
  } catch {
    return null;
  }
};

// projectId removed until the search/column-values APIs support it.
const FUNNEL_FILTERABLE_COLUMN_KEYS = [
  "meterBarCode",
  "components",
  "meterType",
  "meterMake",
  "batchNo",
  "createdBy",
  "simNo",
  "ipAddress",
  "iccid",
  "apn",
  "op",
  "balance",
  "transferredFrom",
  "transferredTo",
  "installationStatus",
  "manufacturingYear",
  "status",
];
const DATE_FILTERABLE_COLUMN_KEYS = ["activationDate"];
const SEARCH_ALL_FIELDS = "all";
const DATE_RANGE_COLUMN = "createdAt";
const COLUMN_VALUES_LIMIT = 200;
const MAX_COLUMN_FILTERS = 15;

const toOption = (item) => {
  if (item && typeof item === "object") {
    const value = item.value ?? item.id ?? item.name ?? item.label;
    return { value: String(value), label: String(item.label ?? item.name ?? value) };
  }
  return { value: String(item), label: String(item) };
};

const buildColumnFilters = (selections, { search, searchField, fromDate, toDate, dateColumns }) => {
  const columnFilters = [];
  if (search && searchField && searchField !== SEARCH_ALL_FIELDS) {
    columnFilters.push({ column: searchField, op: "contains", value: search });
  }
  if (fromDate || toDate) {
    columnFilters.push({
      column: DATE_RANGE_COLUMN,
      op: "between",
      from: fromDate || "1970-01-01",
      to: toDate || formatYmd(new Date()),
    });
  }
  DATE_FILTERABLE_COLUMN_KEYS.forEach((key) => {
    const range = dateColumns?.[key];
    if (range?.fromDate || range?.toDate) {
      columnFilters.push({
        column: key,
        op: "between",
        from: range.fromDate || "1970-01-01",
        to: range.toDate || formatYmd(new Date()),
      });
    }
  });
  FUNNEL_FILTERABLE_COLUMN_KEYS.forEach((key) => {
    if (selections[key]?.size) {
      columnFilters.push({ column: key, op: "in", values: Array.from(selections[key]) });
    }
  });
  return columnFilters.slice(0, MAX_COLUMN_FILTERS);
};

// Ported from src/pages/users/UserList.js's ColumnFilterPopover: `selected` is
// `null` when no explicit choice has been made (every row matches), or a Set
// of explicitly-allowed values. Unchecking the first box expands the implicit
// "everyone" into an explicit Set; re-checking every box collapses back to
// `null`. "None" sets an explicit empty Set (filters to zero rows).
const COLUMN_FILTER_POPOVER_WIDTH = 232;
const ColumnFilterPopover = ({ fetchOptions, selected, onChange, onClose, anchorEl, isRtl, langMode }) => {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [allValues, setAllValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Checkbox / All / None edits stay local until OK.
  const [draft, setDraft] = useState(selected ?? null);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, isRtl ? rect.left : rect.right - COLUMN_FILTER_POPOVER_WIDTH),
        window.innerWidth - COLUMN_FILTER_POPOVER_WIDTH - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  const hasMore = allValues.length >= COLUMN_VALUES_LIMIT;
  useEffect(() => {
    if (q && !hasMore) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setLoadError("");
      try {
        const next = await fetchOptions(q);
        if (cancelled) return;
        setOptions(next);
        if (!q) setAllValues(next.map((o) => o.value));
      } catch (error) {
        console.error("Failed to load column values", error);
        if (!cancelled) {
          setOptions([]);
          setLoadError(error?.message || "Failed to load values");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, q ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, hasMore]);

  const list = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const isChecked = (value) => (draft === null ? true : draft.has(value));
  const toggle = (v) => {
    const base = draft === null ? new Set(allValues) : new Set(draft);
    base.has(v) ? base.delete(v) : base.add(v);
    setDraft(base.size === allValues.length ? null : base);
  };

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width: COLUMN_FILTER_POPOVER_WIDTH }}
      className="z-[80] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="relative mb-2">
        <Search size={13} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRtl ? "right-2" : "left-2"}`} />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={translations[langMode]?.search || "Search…"}
          className={`w-full py-1.5 rounded-md border border-slate-200 text-xs outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${isRtl ? "pr-7 pl-2 text-right" : "pl-7 pr-2"}`}
        />
      </div>
      <div className="max-h-44 overflow-y-auto space-y-0.5 custom-scrollbar">
        {loading && <div className="text-xs px-1.5 py-2 text-slate-400">Loading…</div>}
        {!loading && loadError && <div className="text-xs px-1.5 py-2 text-red-500">{loadError}</div>}
        {!loading && !loadError && list.length === 0 && <div className="text-xs px-1.5 py-2 text-slate-400">No matches</div>}
        {!loading && list.map((o) => (
          <label key={o.value} className="flex items-center gap-2 px-1.5 py-1 text-xs rounded hover:bg-slate-50 cursor-pointer text-slate-700">
            <input type="checkbox" checked={isChecked(o.value)} onChange={() => toggle(o.value)} className="accent-[#236DB4] w-3.5 h-3.5 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{o.label}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
        <div className="flex items-center gap-2 text-xs font-medium">
          <button type="button" onClick={() => setDraft(null)} className="text-[#236DB4] hover:underline">{translations[langMode]?.all || "All"}</button>
          <button type="button" onClick={() => setDraft(new Set())} className="text-slate-400 hover:underline">{translations[langMode]?.none || "None"}</button>
        </div>
        <button type="button" disabled={loading} onClick={() => { onChange(draft); onClose(); }} className="h-7 px-3 rounded-md text-xs font-semibold text-white bg-[#236DB4] hover:bg-[#1d5a96] disabled:opacity-50 disabled:cursor-not-allowed">{translations[langMode]?.ok || "OK"}</button>
      </div>
    </div>,
    document.body
  );
};

const DATE_RANGE_POPOVER_WIDTH = 300;
const DateRangeFilterPopover = ({
  anchorEl,
  onClose,
  fromDate,
  toDate,
  onChangeFrom,
  onChangeTo,
  onClear,
  onApply,
  todayPicker,
  isRtl,
  langMode,
}) => {
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, isRtl ? rect.right - DATE_RANGE_POPOVER_WIDTH : rect.left),
        window.innerWidth - DATE_RANGE_POPOVER_WIDTH - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      // The calendar popups are portaled to document.body via datePickerPopper,
      // so they're siblings of this popover in the DOM, not descendants -- a
      // click inside one would otherwise look like an "outside" click.
      if (event.target.closest?.(".react-datepicker, .datepicker-portal")) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width: DATE_RANGE_POPOVER_WIDTH }}
      className="z-[80] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">{translations[langMode].fromdate}</label>
          <DatePicker
            selected={parseYmd(fromDate)}
            onChange={onChangeFrom}
            placeholderText={translations[langMode].fromdate}
            maxDate={todayPicker}
            selectsStart
            startDate={parseYmd(fromDate)}
            endDate={parseYmd(toDate)}
            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={datePickerPopper}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">{translations[langMode].todate}</label>
          <DatePicker
            selected={parseYmd(toDate)}
            onChange={onChangeTo}
            placeholderText={translations[langMode].todate}
            minDate={parseYmd(fromDate)}
            maxDate={todayPicker}
            selectsEnd
            startDate={parseYmd(fromDate)}
            endDate={parseYmd(toDate)}
            disabled={!fromDate}
            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={datePickerPopper}
          />
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
        <button type="button" onClick={onClear} className="text-xs font-medium text-slate-500 hover:underline">
          {translations[langMode].reset || "Clear"}
        </button>
        <button data-button-intent="primary"
          type="button"
          onClick={onApply}
          className="h-8 px-3 rounded-md text-xs font-semibold text-white bg-[#236DB4] hover:bg-[#1d5a96]"
        >
          {translations[langMode].filter || "Apply"}
        </button>
      </div>
    </div>,
    document.body
  );
};

const COLUMN_DATE_POPOVER_WIDTH = 300;
const YMD_PATTERN = /\d{4}-\d{2}-\d{2}/g;
const isValidYmd = (value) => {
  const date = parseYmd(value);
  return Boolean(date) && formatYmd(date) === value;
};
const formatRangeText = (fromDate, toDate) => (fromDate || toDate ? `${fromDate || "—"} to ${toDate || "—"}` : "");

// Header date filter in the Task List style (typed range + inline range calendar); applies on Apply.
const ColumnDateRangePopover = ({ anchorEl, range, onApply, onClose, todayPicker, isRtl, langMode }) => {
  const [draft, setDraft] = useState({ fromDate: range?.fromDate || "", toDate: range?.toDate || "" });
  const [inputText, setInputText] = useState(formatRangeText(range?.fromDate, range?.toDate));
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, isRtl ? rect.left : rect.right - COLUMN_DATE_POPOVER_WIDTH),
        window.innerWidth - COLUMN_DATE_POPOVER_WIDTH - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  const setRange = (fromDate, toDate) => {
    setDraft({ fromDate, toDate });
    setInputText(formatRangeText(fromDate, toDate));
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setInputText(value);
    if (!value.trim()) {
      setDraft({ fromDate: "", toDate: "" });
      return;
    }
    const [typedFrom = "", typedTo = ""] = value.match(YMD_PATTERN) || [];
    setDraft((prev) => ({
      fromDate: !typedFrom ? "" : isValidYmd(typedFrom) ? typedFrom : prev.fromDate,
      toDate: !typedTo ? "" : isValidYmd(typedTo) ? typedTo : prev.toDate,
    }));
  };

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width: COLUMN_DATE_POPOVER_WIDTH }}
      className="z-[80] flex flex-col items-center bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal tracking-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <input
        type="text"
        value={inputText}
        onChange={handleTextChange}
        placeholder="Select date range (YYYY-MM-DD to YYYY-MM-DD)"
        className="mb-2 w-full min-w-0 h-8 text-xs border rounded-lg px-2.5 bg-slate-50 placeholder:text-slate-400 text-slate-700 font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
      />
      <div className="react-datepicker-inline-container border border-slate-100 rounded-lg overflow-hidden shadow-sm">
        <DatePicker
          selected={null}
          startDate={parseYmd(draft.fromDate)}
          endDate={parseYmd(draft.toDate)}
          onChange={(dates) => {
            const [start, end] = dates || [];
            setRange(start ? formatYmd(start) : "", end ? formatYmd(end) : "");
          }}
          maxDate={todayPicker}
          selectsRange
          inline
        />
      </div>
      <div className="w-full flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => {
            onApply({ fromDate: "", toDate: "" });
            onClose();
          }}
          className="text-[11px] font-medium text-slate-400 hover:text-slate-600 hover:underline transition-colors"
        >
          {translations[langMode]?.reset || "Clear"}
        </button>
        <button
          type="button"
          onClick={() => {
            onApply(draft);
            onClose();
          }}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded-md hover:bg-[#1d5a96] transition-all shadow-sm"
        >
          {translations[langMode]?.filter || "Apply"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export const StockManagement = () => {
  const { theme, selectedOrgId, langMode, projectId } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const meterPerm = getPermissionByScreenCode("METER_INVENTORY");
  const canUpdateStock = Boolean(meterPerm?.canUpdate);
  const canDeleteStock = Boolean(meterPerm?.canDelete);
  const canCreateStock = Boolean(meterPerm?.canCreate);
  const [inventoryDetails, setInventoryDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [openmodal, setOpenModal] = useState(false);
  const [editBarcode, setEditBarcode] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [searchInput, setSearchInput] = useState(() => {
    const persisted = readStockManageFilters();
    return persisted?.searchInput ?? persisted?.meterCode ?? persisted?.toLocation ?? "";
  });
  const [searchScope, setSearchScope] = useState(() => {
    const scope = readStockManageFilters()?.searchScope;
    return scope === "toLocation" ? "transferredTo" : scope || SEARCH_ALL_FIELDS;
  });
  const [columnPrefs, setColumnPrefs] = useState(readStockColumnPrefs);
  const [density, setDensity] = useState(() => {
    const saved = readStockTableView();
    return saved && (saved.density === "compact" || saved.density === "comfortable")
      ? saved.density
      : "comfortable";
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = readStockTableView();
    if (saved && Array.isArray(saved.columnOrder)) {
      const known = saved.columnOrder.filter((id) => DEFAULT_COLUMN_ORDER_STOCK.includes(id));
      const missing = DEFAULT_COLUMN_ORDER_STOCK.filter((id) => !known.includes(id));
      const merged = [...known, ...missing];
      if (merged.length === DEFAULT_COLUMN_ORDER_STOCK.length) return merged;
    }
    return DEFAULT_COLUMN_ORDER_STOCK;
  });
  const [dragColId, setDragColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);
  const columnsButtonRef = useRef(null);
  const rowH = density === "compact" ? "py-1.5" : "py-2.5";

  const [showColumnFilters, setShowColumnFilters] = useState(false);
  // Draws attention to the just-revealed per-column funnel filter icons,
  // mirroring TaskListView.js's "justRevealedFilters" pulse.
  const [justRevealedColumnFilters, setJustRevealedColumnFilters] = useState(false);
  const prevShowColumnFiltersRef = useRef(showColumnFilters);
  useEffect(() => {
    if (showColumnFilters && !prevShowColumnFiltersRef.current) {
      setJustRevealedColumnFilters(true);
      const t = setTimeout(() => setJustRevealedColumnFilters(false), 900);
      prevShowColumnFiltersRef.current = showColumnFilters;
      return () => clearTimeout(t);
    }
    prevShowColumnFiltersRef.current = showColumnFilters;
  }, [showColumnFilters]);
  const [columnFilterSelections, setColumnFilterSelections] = useState(() => {
    const base = Object.fromEntries(FUNNEL_FILTERABLE_COLUMN_KEYS.map((key) => [key, null]));
    const persisted = readStockManageFilters()?.columnFilterSelections;
    if (!persisted || typeof persisted !== "object") return base;
    FUNNEL_FILTERABLE_COLUMN_KEYS.forEach((key) => {
      if (Array.isArray(persisted[key]) && persisted[key].length) base[key] = new Set(persisted[key]);
    });
    return base;
  });
  const [openFilterPopoverColId, setOpenFilterPopoverColId] = useState(null);
  const filterButtonRefs = useRef({});
  const hasActiveColumnFilters = FUNNEL_FILTERABLE_COLUMN_KEYS.some(
    (key) => { const s = columnFilterSelections[key]; return s instanceof Set ? s.size > 0 : Boolean(s?.length); }
  );
  const [showDateRangePopover, setShowDateRangePopover] = useState(false);
  // Dates picked in the popover are only applied to `filters` on Apply Filters.
  const [draftDateRange, setDraftDateRange] = useState({ fromDate: "", toDate: "" });
  const dateRangeButtonRef = useRef(null);

  const baseHeaders = [
    { value: "meterBarCode", label: translations[langMode].meterBarCode },
    { value: "projectId", label: translations[langMode].project },
    { value: "components", label: translations[langMode].components },
    { value: "meterType", label: translations[langMode].meterType },
    { value: "manufacturingYear", label: translations[langMode].manufacturingYear },
    { value: "meterMake", label: translations[langMode].meterMake },
    { value: "activationDate", label: translations[langMode].activationDate },
    { value: "batchNo", label: translations[langMode].batchNo },
    { value: "installationStatus", label: translations[langMode].installationStatus },
    { value: "createdBy", label: translations[langMode].createBy },
    { value: "createdAt", label: translations[langMode].createdAt },
    { value: "simNo", label: translations[langMode].simNo },
    { value: "ipAddress", label: translations[langMode].ipAddress },
    { value: "iccid", label: translations[langMode].iccid },
    { value: "apn", label: translations[langMode].apn },
    { value: "op", label: translations[langMode].op },
    { value: "balance", label: translations[langMode].balance },
    { value: "status", label: translations[langMode].status },
    { value: "simDetailUpdatedAt", label: translations[langMode].simDetailUpdatedAt },
    { value: "transferredFrom", label: translations[langMode].transferredFrom },
    { value: "transferredTo", label: translations[langMode].transferredTo },
  ];
  const baseHeaderKeys = baseHeaders.map(header => header.value.trim());

  const dynamicHeaders =
    inventoryDetails && inventoryDetails?.length > 0
      ? Object.keys(inventoryDetails[0])
        .filter(
          (key) =>
            key !== "id" &&
            key !== "organizationId" &&
            !baseHeaderKeys.includes(key.trim())
        )
        .map((key) => ({
          value: key,
          label: key,
        }))
      : [];

  const pinnedHeader = baseHeaders.find((header) => header.value === "meterBarCode");
  const headersByValue = new Map(baseHeaders.map((header) => [header.value, header]));
  const searchFieldOptions = [
    { value: SEARCH_ALL_FIELDS, label: translations[langMode]?.allFields ?? "All Fields" },
    ...FUNNEL_FILTERABLE_COLUMN_KEYS.map((key) => ({ value: key, label: headersByValue.get(key)?.label ?? key })),
  ];
  const searchScopeLabel = searchFieldOptions.find((option) => option.value === searchScope)?.label ?? "";
  const toggleableHeaders = columnOrder
    .map((value) => headersByValue.get(value))
    .filter(Boolean);
  const allColumnHeaders = [...toggleableHeaders, ...dynamicHeaders];
  const allColumnKeys = allColumnHeaders.map((header) => header.value);
  const effectiveVisibleColumnKeys = (() => {
    const { visibleColumnKeys, knownHeaderKeys, selectionMode } = columnPrefs;
    const orderedExisting = visibleColumnKeys.filter((key) => allColumnKeys.includes(key));
    if (selectionMode === "all" || !orderedExisting.length) return allColumnKeys;
    // A column added after the user last applied a selection was never offered, so it shows.
    const neverOfferedKeys = knownHeaderKeys.length
      ? allColumnKeys.filter((key) => !orderedExisting.includes(key) && !knownHeaderKeys.includes(key))
      : [];
    return [...orderedExisting, ...neverOfferedKeys];
  })();
  const visibleColumnSet = new Set(effectiveVisibleColumnKeys);
  const visibleToggleableHeaders = toggleableHeaders.filter((header) => visibleColumnSet.has(header.value));
  const tableHeaders = [
    ...(pinnedHeader ? [pinnedHeader] : []),
    ...visibleToggleableHeaders,
    ...dynamicHeaders.filter((header) => visibleColumnSet.has(header.value)),
  ];

  const handleColumnSelectionApply = (nextVisibleKeys = []) => {
    const normalizedKeys = Array.isArray(nextVisibleKeys) ? nextVisibleKeys : [];
    const isAllSelected =
      allColumnKeys.length > 0 &&
      normalizedKeys.length === allColumnKeys.length &&
      allColumnKeys.every((key) => normalizedKeys.includes(key));
    const nextPrefs = {
      visibleColumnKeys: normalizedKeys,
      knownHeaderKeys: allColumnKeys,
      selectionMode: isAllSelected ? "all" : DEFAULT_COLUMN_SELECTION_MODE,
    };
    setColumnPrefs(nextPrefs);
    persistStockColumnPrefs(nextPrefs);
  };

  const handleColumnDrop = (targetId) => {
    if (dragColId && dragColId !== targetId) {
      setColumnOrder((order) => {
        const next = [...order];
        const from = next.indexOf(dragColId);
        const to = next.indexOf(targetId);
        if (from === -1 || to === -1) return order;
        next.splice(from, 1);
        next.splice(to, 0, dragColId);
        return next;
      });
    }
    setDragColId(null);
    setDragOverColId(null);
  };

  useEffect(() => {
    try {
      localStorage.setItem(
        STOCK_TABLE_VIEW_KEY,
        JSON.stringify({ density, columnOrder })
      );
    } catch {
      // ignore storage errors (e.g. private browsing / quota)
    }
  }, [density, columnOrder]);

  const [filters, setFilters] = useState(() => {
    const persisted = readStockManageFilters()?.filters;
    return {
      fromDate: persisted?.fromDate || "",
      toDate: persisted?.toDate || "",
      search: persisted?.search ?? persisted?.meterBarCode ?? persisted?.toLocation ?? "",
      searchField:
        persisted?.searchField ??
        (persisted?.toLocation ? "transferredTo" : persisted?.meterBarCode ? "meterBarCode" : SEARCH_ALL_FIELDS),
      dateColumns: persisted?.dateColumns && typeof persisted.dateColumns === "object" ? persisted.dateColumns : {},
    };
  });
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const hasDateRangeFilter = Boolean(filters.fromDate || filters.toDate);

  useEffect(() => {
    try {
      const payload = {
        filters,
        searchInput,
        searchScope,
        columnFilterSelections: Object.fromEntries(
          FUNNEL_FILTERABLE_COLUMN_KEYS.map((key) => [
            key,
            columnFilterSelections[key] ? Array.from(columnFilterSelections[key]) : null,
          ])
        ),
      };
      const json = JSON.stringify(payload);
      sessionStorage.setItem(STOCK_MANAGE_TABLE_FILTERS_KEY, json);
      localStorage.setItem(STOCK_MANAGE_TABLE_FILTERS_KEY, json);
    } catch (error) {
      console.error("Error persisting stock table filters:", error);
    }
  }, [filters, searchInput, searchScope, columnFilterSelections]);

  const totalPages = Math.ceil(totalValue / pageSize) - 1;
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };
  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    const pageIds = (inventoryDetails || []).map((item) => item?.id).filter(Boolean);
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedItems((prev) => [...new Set([...prev, ...pageIds])]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  useEffect(() => {
    setSelectAll(
      inventoryDetails.length > 0 &&
      inventoryDetails.every((item) => item?.id && selectedItems.includes(item.id))
    );
  }, [selectedItems, inventoryDetails]);

  useEffect(() => {
    setSelectAll(false);
    setSelectedItems([]);
  }, [currentPage, pageSize]);

  const showDeleteModal = () => {
    setPendingDeleteIds(selectedItems);
    setDeleteModalOpen(true);
  };

  const handleDeleteRow = (id) => {
    setPendingDeleteIds([id]);
    setDeleteModalOpen(true);
  };

  const latestRequestRef = useRef(0);
  const fetchInventoryData = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const value = await searchMeterInventory({
        organizationId: selectedOrgId,
        projectId: null,
        page: currentPage,
        size: pageSize,
        search: filters.searchField === SEARCH_ALL_FIELDS ? filters.search : "",
        columnFilters: buildColumnFilters(columnFilterSelections, filters),
      });
      if (requestId !== latestRequestRef.current) return;
      const list = value?.meterInventoryList ?? value?.content ?? value?.list ?? (Array.isArray(value) ? value : []);
      setInventoryDetails(list);
      setTotalValue(value?.totalSize ?? value?.totalElements ?? value?.total ?? list.length);
    } catch (error) {
      console.error("Failed to fetch inventory data", error);
      if (requestId === latestRequestRef.current) {
        setInventoryDetails([]);
        setTotalValue(0);
        toast.error(error?.message || translations[langMode]?.somethingWentWrong || "Failed to load data");
      }
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [currentPage, pageSize, filters, columnFilterSelections, selectedOrgId]);

  const refreshInventory = fetchInventoryData;

  useEffect(() => {
    fetchInventoryData();
  }, [fetchInventoryData]);

  const handleDateColumnFilterChange = (key, range) => {
    const hasRange = Boolean(range?.fromDate || range?.toDate);
    setFilters((prev) => ({ ...prev, dateColumns: { ...(prev.dateColumns || {}), [key]: hasRange ? range : null } }));
    setCurrentPage(0);
  };

  const handleColumnFilterChange = (key, nextSelection) => {
    const normalized = nextSelection && nextSelection.size > 0 ? nextSelection : null;
    setColumnFilterSelections((prev) => ({ ...prev, [key]: normalized }));
    setCurrentPage(0);
  };

  const fetchColumnOptions = useCallback(
    async (column, search) => {
      const columnConditions = FUNNEL_FILTERABLE_COLUMN_KEYS.filter(
        (key) => key !== column && columnFilterSelections[key]?.size
      ).map((key) => ({ column: key, value: Array.from(columnFilterSelections[key]) }));
      const value = await getMeterInventoryColumnValues({
        organizationId: selectedOrgId,
        projectId: null,
        column,
        search: search || "",
        limit: COLUMN_VALUES_LIMIT,
        columnConditions,
      });
      const values = Array.isArray(value)
        ? value
        : Array.isArray(value?.values)
          ? value.values
          : Array.isArray(value?.list)
            ? value.list
            : value && typeof value === "object"
              ? Object.entries(value).map(([id, label]) => ({ value: id, label: label ?? id }))
              : [];
      return values.filter((item) => item !== null && item !== undefined && item !== "").map(toOption);
    },
    [columnFilterSelections, selectedOrgId]
  );

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleApplyFilters = () => {
    setCurrentPage(0);
    refreshInventory();
  };

  useEffect(() => {
    if (selectedOrgId) {
      setCurrentPage(0);
      setPageSize(10);
    }
  }, [selectedOrgId]);

  const renderCells = (item) => {
    return tableHeaders.map((header) => {
      let value = item[header.value];
      if (header.value === "createdAt" || header.value === "activationDate") {
        value = value ? formattedDate(value) : "-";
      } else {
        value =
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "")
            ? "-"
            : value;
      }
      return (
        <td key={header.value} className={`px-3 sm:px-4 ${rowH} text-nowrap text-xs`}>
          {value}
        </td>
      );
    });
  };

  const handleEditClick = (item) => {
    setEditBarcode(item);
  };

  const UploadReport = () => {
    setOpen(true);
  };
  const DownloadModal = () => {
    setOpenModal(true);
  };
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const deleteFunction = async (selectedItems) => {
    setLoading(true);
    console.log(selectedItems);
    try {
      const data = await deleteinventory(selectedItems);
      if (data.header.code === 600) {
        toast.success(data.body.value);
        refreshInventory();
        handleCloseDeleteModal();
        setLoading(false);
        setSelectAll(false);
        setSelectedItems([]);
      } else {
        toast.error(data.body.value);
        refreshInventory();
        handleCloseDeleteModal();
        setLoading(false);
        setSelectAll(false);
        setSelectedItems([]);
      }
    } catch (error) {
      console.error("Failed to deleting the record:", error);
      toast.error(translations[langMode].errorOccurredWhileDeletingRecord);
      setLoading(false);
    }
  };

  const applySearch = (search, searchField) => {
    setFilters((prev) =>
      prev.search === search && prev.searchField === searchField ? prev : { ...prev, search, searchField }
    );
    setCurrentPage(0);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value === "") applySearch("", searchScope);
  };

  const handleSearchScopeChange = (nextScope) => {
    setSearchScope(nextScope);
    setSearchInput("");
    applySearch("", nextScope);
  };

  const handleSearchSubmit = () => {
    applySearch(searchInput.trim(), searchScope);
  };

  useEffect(() => {
    if (!searchInput) return;
    const timeout = setTimeout(() => {
      handleSearchSubmit();
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput, searchScope]);

  const hasToolbarDateRange = Boolean((filters.fromDate && String(filters.fromDate).trim()) || (filters.toDate && String(filters.toDate).trim()));
  const hasSearchFilter = Boolean(filters.search && String(filters.search).trim());
  const activeFunnelFilterCount = FUNNEL_FILTERABLE_COLUMN_KEYS.filter((key) => {
    const sel = columnFilterSelections?.[key];
    if (!sel) return false;
    if (sel instanceof Set) return sel.size > 0;
    if (Array.isArray(sel)) return sel.length > 0;
    return false;
  }).length;

  const activeDateColumnFilterCount = DATE_FILTERABLE_COLUMN_KEYS.filter((key) => {
    if (key === DATE_RANGE_COLUMN && hasToolbarDateRange) return false; const range = filters.dateColumns?.[key];
    return Boolean(range?.fromDate || range?.toDate);
  }).length;

  const isDefaultToolbarState =
    !hasSearchFilter &&
    !hasToolbarDateRange &&
    !searchInput?.trim() &&
    activeFunnelFilterCount === 0 &&
    searchScope === SEARCH_ALL_FIELDS &&
    density === "comfortable" &&
    columnPrefs.selectionMode === "all" &&
    columnOrder.join() === DEFAULT_COLUMN_ORDER_STOCK.join() &&
    !showColumnFilters &&
    !hasActiveColumnFilters &&
    activeDateColumnFilterCount === 0;

  const appliedFilterCount =
    activeDateColumnFilterCount +
    (hasSearchFilter ? 1 : 0) +
    (hasToolbarDateRange ? 1 : 0) +
    activeFunnelFilterCount;

  const handleResetToolbar = () => {
    setSearchInput("");
    setSearchScope(SEARCH_ALL_FIELDS);
    setFilters({ fromDate: "", toDate: "", search: "", searchField: SEARCH_ALL_FIELDS, dateColumns: {} });
    setColumnPrefs(ALL_COLUMNS_PREFS);
    persistStockColumnPrefs(ALL_COLUMNS_PREFS);
    setColumnOrder(DEFAULT_COLUMN_ORDER_STOCK);
    setDensity("comfortable");
    setColumnFilterSelections(
      Object.fromEntries(FUNNEL_FILTERABLE_COLUMN_KEYS.map((key) => [key, null]))
    );
    setOpenFilterPopoverColId(null);
    setShowColumnFilters(false);
    setShowDateRangePopover(false); setSelectAll(false); setSelectedItems([]);
    setCurrentPage(0);
    try {
      sessionStorage.removeItem(STOCK_MANAGE_TABLE_FILTERS_KEY);
      localStorage.removeItem(STOCK_MANAGE_TABLE_FILTERS_KEY);
    } catch {
      // ignore storage errors (e.g. private browsing / quota)
    }
  };

  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true, // Use 12-hour format with AM/PM
    };

    const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  return (
    <div
      dir={dir}
      className={`min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex flex-col overflow-y-auto sm:overflow-hidden ${theme === "dark"
        ? "dark-mode"
        : theme === "high-contrast"
          ? "high-contrast"
          : ""
        }`}
    >
      <div
        className={`shrink-0 flex flex-col gap-3 mx-2 mt-2 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1
              className={`text-base font-bold text-slate-800 leading-tight sm:text-lg ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
                }`}
            >
              {translations[langMode].stockmanag}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {translations[langMode]?.stockManagementSubtitle ?? "View, track, and manage your meter inventory stock."}
            </p>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:justify-start sm:ms-auto">
            <CommonButton colorIntent="primary"
              onClick={DownloadModal}
              theme={theme}
              variant="primary"
              tooltipText={translations[langMode].downloadincentoryreport}
              tooltipPosition="top"
              wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
              className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
            >
              <Download size={16} className="shrink-0" />
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">{translations[langMode].download}</span>
            </CommonButton>
            {canCreateStock && (
              <CommonButton colorIntent="success"
                onClick={UploadReport}
                theme={theme}
                variant="success"
                tooltipText={translations[langMode].uploadtheinventorysheet}
                tooltipPosition="top"
                wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                className="w-full !rounded-lg !h-10 !min-h-[40px] !px-2.5 sm:!px-3 !gap-1.5 sm:!gap-2 justify-center"
              >
                <img src='https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344903/arrow-undo-circle_uxixpq.png' className="h-4 w-4 sm:h-5 sm:w-5 shrink-0 object-contain" alt="" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap truncate">{translations[langMode].uploadSheet}</span>
              </CommonButton>
            )}
          </div>
        </div>

        <div
          className={`flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 ${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
        >
          {/* Mobile: total count, Filters, Reset, Columns, Compact share one
              horizontally-scrollable line (nowrap). Desktop: this wrapper
              itself disappears (display:contents) so its children rejoin the
              outer flex-wrap row as plain siblings of Date Range/Search, and
              Reset/Columns/Compact's own `sm:order-1` pushes them after Date
              Range/Search -- reproducing the previous two-group desktop
              layout without changing any desktop classes on those groups. */}
          <div className="flex w-full min-w-0 items-center justify-between gap-1.5 sm:gap-2 sm:contents">
            <div className="hidden sm:flex h-9 px-3 rounded-lg border border-slate-200 items-center gap-1.5 text-sm shrink-0 text-slate-700">
              <HoverTooltip text={translations[langMode]?.totalItemsTooltip ?? "Total number of records matching the current filters."}>
                <span>{loading ? "—" : totalValue} {translations[langMode].items}</span>
              </HoverTooltip>
            </div>

            <HoverTooltip text={translations[langMode]?.filtersToggleTooltip ?? "Apply filters from the table header"} className="relative flex-1 min-w-0 sm:flex-initial shrink-0">
              <button data-table-control="filters" aria-pressed={showColumnFilters}
                type="button"
                onClick={() => setShowColumnFilters((v) => !v)}
                className="h-9 w-full sm:w-auto px-2.5 sm:px-3 rounded-lg border text-xs sm:text-sm font-medium flex items-center justify-center gap-1.5 sm:gap-2 bg-white"
                style={{
                  borderColor: showColumnFilters ? "#93C5FD" : "#E2E8F0",
                  color: showColumnFilters ? "#1D4ED8" : "#334155",
                }}
              >
                <Filter size={13} /> <span>{translations[langMode]?.columnFiltersLabel ?? "Column Filters"}</span>
              </button>
            </HoverTooltip>

            <HoverTooltip
              text={translations[langMode]?.resetAllAppliedFiltersTooltip ?? "Clear all applied filters"}
              className="relative inline-flex shrink-0 sm:order-1 sm:ms-auto"
            >
              <div className="relative">
                <button data-button-intent="warning"
                  type="button"
                  onClick={handleResetToolbar}
                  disabled={isDefaultToolbarState}
                  className="h-9 sm:h-8 px-2.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-slate-50"
                >
                  <RotateCcw size={13} /> <span className="hidden md:inline">{translations[langMode].reset || "Reset"}</span>
                </button>
                {appliedFilterCount > 0 && (
                  <span
                    className={`absolute -top-2 ${isRtl ? "-left-2" : "-right-2"} z-10 grid min-w-[20px] h-5 px-1 place-items-center rounded-full bg-[#ff3b30] text-white text-[11px] font-semibold leading-[20px] tabular-nums pointer-events-none`}
                    aria-label={`${appliedFilterCount} applied filters`}
                  >
                    {appliedFilterCount > 99 ? "99+" : appliedFilterCount}
                  </span>
                )}
              </div>
            </HoverTooltip>

            <div ref={columnsButtonRef} className="relative h-9 sm:h-8 flex-1 min-w-0 sm:flex-initial shrink-0 sm:order-1">
              <HoverTooltip text={translations[langMode]?.columnsTooltip ?? "Choose which columns appear in the table."} className="relative w-full">
                <ColumnsButton
                  type="button"
                  onClick={() => setShowColPicker((v) => !v)}
                  className="h-9 sm:h-8 w-full sm:w-auto px-2 sm:px-2.5 rounded-lg border text-xs font-medium flex items-center justify-center gap-1"

                  aria-expanded={showColPicker}
                >
                  <Columns3 size={13} />
                  <span className="hidden md:inline">{translations[langMode].columns || "Columns"}</span>
                  <span className="inline-flex h-5 min-w-[30px] items-center justify-center rounded-full bg-slate-100 px-1 text-[11px] leading-none tabular-nums text-slate-500">
                    {effectiveVisibleColumnKeys.length}/{allColumnHeaders.length}
                  </span>
                </ColumnsButton>
              </HoverTooltip>
              <ColumnsCustomizeModal
                open={showColPicker}
                onClose={() => setShowColPicker(false)}
                tableHeaders={allColumnHeaders}
                visibleColumnKeys={effectiveVisibleColumnKeys}
                onApply={handleColumnSelectionApply}
                langMode={langMode}
                anchorRef={columnsButtonRef}
              />
            </div>

            <HoverTooltip
              text={translations[langMode]?.toggleDensity || "Toggle density"}
              className="relative inline-flex shrink-0 sm:order-1"
            >
              <button
                type="button"
                onClick={() => setDensity((d) => (d === "compact" ? "comfortable" : "compact"))}
                className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"
              >
                {density === "compact" ? <Rows3 size={13} /> : <LayoutGrid size={13} />}
              </button>
            </HoverTooltip>
          </div>

          <div ref={dateRangeButtonRef} className="relative h-9 w-full sm:w-auto">
              <HoverTooltip
                text={translations[langMode]?.dateRangeTooltip ?? "Filter by activation date range"}
                className="relative inline-flex w-full sm:w-auto"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (!showDateRangePopover) setDraftDateRange({ fromDate: filters.fromDate, toDate: filters.toDate });
                    setShowDateRangePopover((v) => !v);
                  }}
                  className="h-9 w-full sm:w-auto px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 bg-white whitespace-nowrap"
                  style={{
                    borderColor: showDateRangePopover || hasDateRangeFilter ? "#93C5FD" : "#E2E8F0",
                    background: showDateRangePopover ? "#EFF6FF" : "white",
                    color: showDateRangePopover || hasDateRangeFilter ? "#1D4ED8" : "#334155",
                  }}
                >
                  <Calendar size={14} />
                  <span>
                    {hasDateRangeFilter
                      ? `${formatShortDate(filters.fromDate) || "…"} – ${formatShortDate(filters.toDate) || "…"}`
                      : translations[langMode]?.dateRange ?? "Date Range"}
                  </span>
                  <ChevronDown size={14} className={showDateRangePopover ? "rotate-180" : ""} />
                </button>
              </HoverTooltip>
              {showDateRangePopover && (
                <DateRangeFilterPopover
                  anchorEl={dateRangeButtonRef.current}
                  onClose={() => setShowDateRangePopover(false)}
                  fromDate={draftDateRange.fromDate}
                  toDate={draftDateRange.toDate}
                  onChangeFrom={(date) => {
                    setDraftDateRange((prev) => {
                      const nextFrom = date ? formatYmd(date) : "";
                      let nextTo = prev.toDate;
                      if (date && parseYmd(prev.toDate) && parseYmd(prev.toDate) < date) {
                        nextTo = formatYmd(date);
                      }
                      return { ...prev, fromDate: nextFrom, toDate: nextTo };
                    });
                  }}
                  onChangeTo={(date) =>
                    setDraftDateRange((prev) => ({ ...prev, toDate: date ? formatYmd(date) : "" }))
                  }
                  onClear={() => {
                    setFilters((prev) => ({ ...prev, fromDate: "", toDate: "" }));
                    setCurrentPage(0);
                    setShowDateRangePopover(false); setSelectAll(false); setSelectedItems([]);
                  }}
                  onApply={() => {
                    setShowDateRangePopover(false); setSelectAll(false); setSelectedItems([]);
                    if (draftDateRange.fromDate === filters.fromDate && draftDateRange.toDate === filters.toDate) {
                      handleApplyFilters();
                    } else {
                      setCurrentPage(0);
                      setFilters((prev) => ({ ...prev, fromDate: draftDateRange.fromDate, toDate: draftDateRange.toDate }));
                    }
                  }}
                  todayPicker={todayPicker}
                  isRtl={isRtl}
                  langMode={langMode}
                />
              )}
            </div>

            <div className="flex h-9 w-full sm:flex-1 sm:min-w-[260px] sm:max-w-[360px]">
              <HoverTooltip text={translations[langMode]?.searchTooltip ?? "Search by the selected field. Press Enter to search."}>
                <SearchFieldPicker
                  options={searchFieldOptions}
                  activeValue={searchScope}
                  onSelect={handleSearchScopeChange}
                  searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                  isRtl={isRtl}
                />
              </HoverTooltip>
              <div className="relative flex-1 min-w-0">
                <Search
                  size={15}
                  className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${isRtl ? "right-3" : "left-3"}`}
                />
                <input
                    id="account-search"
                    type="text"
                    autoComplete="off"
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit();
                      }
                    }}
                    placeholder={typeof window !== "undefined" && window.innerWidth < 500 ? `${translations[langMode]?.search || "Search"}...` : `${translations[langMode]?.search || "Search"} ${
                      searchScopeLabel
                    }...`}
                    title={`${translations[langMode]?.search || "Search"} ${
                      searchScopeLabel
                    }`}
                    aria-label={`${translations[langMode]?.search || "Search"} ${
                      searchScopeLabel
                    }`}
                    className={`block w-full h-9 text-sm border border-slate-200 outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
                      isRtl ? "pr-9 pl-3 border-r-0 rounded-l-lg" : "pl-9 pr-3 border-l-0 rounded-r-lg"
                    }`}
                  />
              </div>
            </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full flex flex-col">
        <div
          className={`mx-2 mb-2 mt-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 ${theme === "dark"
            ? "dark-mode border "
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }`}
        >
            <div className="relative z-20 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
              {selectedItems.length > 0 ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                    {`${selectedItems.length} ${translations[langMode]?.selected ?? "selected"}`}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    className="text-xs text-slate-500 hover:underline"
                  >
                    {translations[langMode]?.clear ?? "Clear"}
                  </button>
                  <CommonButton colorIntent="danger"
                    onClick={showDeleteModal}
                    theme={theme}
                    variant="danger"
                    tooltipText={translations[langMode].proceedwithdeleaction}
                    tooltipPosition="top"
                    className="!h-8 !min-h-[32px] !px-3"
                  >
                    <Trash2 size={14} />
                    {translations[langMode].delete}
                  </CommonButton>
                </div>
              ) : (
                <p className="flex items-center gap-2 text-xs text-slate-500 sm:gap-4">
                  <span className="text-xs text-slate-500 whitespace-nowrap sm:hidden">
                    {`${startIndex + 1}-${Math.min(endIndex, totalValue)} / ${totalValue}`}
                  </span>
                  <span className="hidden sm:inline whitespace-nowrap">
                    {`${translations[langMode].showing || "Showing"} ${startIndex + 1}-${Math.min(
                      endIndex,
                      totalValue
                    )} ${translations[langMode].of || "of"} ${totalValue}`}
                  </span>
                </p>
              )}

              <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
                <div className="flex items-center gap-1.5">
                  <label htmlFor="pageSize" className="hidden sm:inline whitespace-nowrap">
                    {translations[langMode].rows}
                  </label>
                  <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
                </div>
                <span className="pager-nav flex items-center gap-1">
                  <PagerArrowButton
                    direction="prev"
                    isRtl={isRtl}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                    disabled={currentPage === 0}
                  />
                  <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                    {currentPage + 1} / {Math.max(totalPages + 1, 1)}
                  </span>
                  <PagerArrowButton
                    direction="next"
                    isRtl={isRtl}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                </span>
              </div>
            </div>

          {loading && <Spinner />}

          <TableScrollShell theme={theme} className="w-full flex-1 min-h-0" innerClassName="task-list-scrollbar">
              <table
                className={`w-full min-w-[980px] border-collapse text-xs text-center text-gray-500 ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
                dir={dir}
              >
                <thead
                  className={`sticky top-0 z-10 border-b ${theme === "dark"
                    ? "bg-gray-800 text-gray-200 border-gray-700"
                    : theme === "high-contrast"
                      ? "high-contrast bg-gray-100"
                      : "border-slate-200 bg-slate-50 text-xs text-slate-700"
                    }`}
                >
                  <tr>
                    {canDeleteStock && (
                      <th className="px-3 sm:px-4 py-2.5 sm:py-3.5">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 accent-blue-500"
                        />
                      </th>
                    )}
                    <th className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-400 text-center">
                      NO.
                    </th>
                    {(canUpdateStock || canDeleteStock) && (
                      <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                        {translations[langMode].Action}
                      </th>
                    )}
                    {tableHeaders.map((header) => {
                      const isDraggable = visibleToggleableHeaders.includes(header);
                      const isToolbarDateColumn = header.value === DATE_RANGE_COLUMN;
                      const isDateFilterable =
                        showColumnFilters && (isToolbarDateColumn || DATE_FILTERABLE_COLUMN_KEYS.includes(header.value));
                      const columnDateRange = filters.dateColumns?.[header.value];
                      const isFilterable =
                        isDateFilterable ||
                        (showColumnFilters && FUNNEL_FILTERABLE_COLUMN_KEYS.includes(header.value));
                      const hasActiveSelection = isToolbarDateColumn
                        ? hasDateRangeFilter
                        : isDateFilterable
                          ? Boolean(columnDateRange?.fromDate || columnDateRange?.toDate)
                          : Boolean(columnFilterSelections[header.value]);
                      return (
                        <th
                          key={header.value}
                          scope="col"
                          draggable={isDraggable}
                          onDragStart={() => isDraggable && setDragColId(header.value)}
                          onDragOver={(e) => {
                            if (!isDraggable) return;
                            e.preventDefault();
                            setDragOverColId(header.value);
                          }}
                          onDragLeave={() => setDragOverColId((c) => (c === header.value ? null : c))}
                          onDrop={() => isDraggable && handleColumnDrop(header.value)}
                          onDragEnd={() => {
                            setDragColId(null);
                            setDragOverColId(null);
                          }}
                          style={{
                            background: dragOverColId === header.value ? "#EFF6FF" : undefined,
                            opacity: dragColId === header.value ? 0.4 : 1,
                          }}
                          className={`relative px-3 sm:px-4 py-2.5 sm:py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 whitespace-nowrap ${isDraggable ? "cursor-grab active:cursor-grabbing select-none" : ""}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {isDraggable && <GripVertical size={12} className="opacity-40 shrink-0" />}
                            {header?.label}
                            {isFilterable && (
                              <span className={justRevealedColumnFilters ? "inline-flex animate-filter-pulse" : "inline-flex"}>
                                <button
                                  type="button"
                                  ref={(el) => { filterButtonRefs.current[header.value] = el; }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenFilterPopoverColId((prev) =>
                                      prev === header.value ? null : header.value
                                    );
                                  }}
                                  className={`ms-0.5 inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${hasActiveSelection ? "text-[#236DB4] bg-blue-50" : "text-[#236DB4] hover:bg-blue-50"}`}
                                >
                                  <Filter
                                    size={12}
                                    fill={hasActiveSelection ? "currentColor" : "none"}
                                  />
                                </button>
                              </span>
                            )}
                          </span>
                          {isDateFilterable && openFilterPopoverColId === header.value && (
                            <ColumnDateRangePopover
                              anchorEl={filterButtonRefs.current[header.value]}
                              isRtl={isRtl}
                              langMode={langMode}

                              range={
                                isToolbarDateColumn
                                  ? { fromDate: filters.fromDate, toDate: filters.toDate }
                                  : columnDateRange
                              }
                              onApply={(range) => {
                                if (!isToolbarDateColumn) {
                                  handleDateColumnFilterChange(header.value, range);
                                  return;
                                }
                                setFilters((prev) =>
                                  prev.fromDate === range.fromDate && prev.toDate === range.toDate
                                    ? prev
                                    : { ...prev, fromDate: range.fromDate, toDate: range.toDate }
                                );
                                setCurrentPage(0);
                              }}
                              onClose={() => setOpenFilterPopoverColId(null)}
                              todayPicker={todayPicker}


                            />
                          )}
                          {isFilterable && !isDateFilterable && openFilterPopoverColId === header.value && (
                            <ColumnFilterPopover
                              fetchOptions={(search) => fetchColumnOptions(header.value, search)}
                              selected={columnFilterSelections[header.value]}
                              onChange={(next) => handleColumnFilterChange(header.value, next)}
                              onClose={() => setOpenFilterPopoverColId(null)}
                              anchorEl={filterButtonRefs.current[header.value]}
                              isRtl={isRtl}
                              langMode={langMode}

                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {inventoryDetails.length > 0 ? (
                    inventoryDetails.map((item, index) => (
                    <tr
                      key={index}
                      className={`text-xs border-b border-slate-100 transition-colors duration-150 ${theme === "dark"
                        ? "dark-mode hover:bg-gray-800"
                        : theme === "high-contrast"
                          ? "high-contrast"
                          : "bg-white hover:bg-slate-50/70"
                        }`}
                    >
                      {canDeleteStock && (
                        <td className={`px-3 sm:px-4 ${rowH}`}>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="w-4 h-4 accent-blue-500"
                          />
                        </td>
                      )}
                      <td className={`w-[60px] min-w-[60px] max-w-[60px] px-0 ${rowH} text-xs text-[#70788f] whitespace-nowrap font-mono text-center`}>
                        {((currentPage + 1) - 1) * pageSize + index + 1}
                      </td>
                      {(canUpdateStock || canDeleteStock) && (
                        <td className={`px-3 sm:px-4 ${rowH} text-center`}>
                          <div className="flex items-center justify-center gap-1">
                            {canUpdateStock && (
                              <ActionIconButton
                                onClick={() => handleEditClick(item.meterBarCode)}
                                tone="blue"
                                ariaLabel={translations[langMode].edit}
                                tooltipText={translations[langMode].edit || "Edit"}
                                tooltipPosition="top"
                                className="!h-7 !w-7 !rounded-md"
                              >
                                <Pencil size={14} />
                              </ActionIconButton>
                            )}
                            {canDeleteStock && (
                              <ActionIconButton
                                onClick={() => handleDeleteRow(item.id)}
                                tone="red"
                                ariaLabel={translations[langMode].delete}
                                tooltipText={translations[langMode].delete || "Delete"}
                                tooltipPosition="top"
                                className="!h-7 !w-7 !rounded-md"
                              >
                                <Trash2 size={14} />
                              </ActionIconButton>
                            )}
                          </div>
                        </td>
                      )}
                      {renderCells(item)}
                    </tr>
                    ))
                  ) : !loading ? (
                    <tr>
                      <td
                        colSpan={(canDeleteStock ? 1 : 0) + 1 + tableHeaders.length + ((canUpdateStock || canDeleteStock) ? 1 : 0)}
                        className="py-16 h-[200px] align-middle"
                      >
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
                            {appliedFilterCount > 0 ? <SearchX size={22} /> : <Inbox size={22} />}
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {appliedFilterCount > 0
                              ? (translations[langMode]?.noRecordsMatchSearch ?? "No records match your search")
                              : translations[langMode].noDatavb}
                          </div>
                          <div className="text-xs mt-1 max-w-xs text-slate-400">
                            {appliedFilterCount > 0
                              ? (translations[langMode]?.tryAdjustingFilters ?? "Try adjusting your filters or search terms to find what you're looking for.")
                              : (translations[langMode]?.getStartedUploadingSheet ?? "Get started by uploading your inventory sheet.")}
                          </div>
                          {appliedFilterCount > 0 ? (
                            <button data-button-intent="warning"
                              type="button"
                              onClick={handleResetToolbar}
                              className="mt-3 h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                            >
                              {translations[langMode]?.clearFilters ?? "Clear filters"}
                            </button>
                          ) : (
                            canCreateStock && (
                              <button data-button-intent="success"
                                type="button"
                                onClick={UploadReport}
                                className="mt-3 h-8 px-3 rounded-lg text-xs font-medium text-white bg-blue-600"
                              >
                                {translations[langMode].uploadSheet}
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
          </TableScrollShell>
        </div>
      </div>

      <UploadExcelReport
        open={open}
        setOpen={setOpen}
        fetchInventoryData={refreshInventory}
      />
      <DownloadReport openmodal={openmodal} setOpenModal={setOpenModal} />
      <UpdateStock
        open={editBarcode !== null}
        barcode={editBarcode}
        onClose={() => setEditBarcode(null)}
        onSaved={() => {
          setEditBarcode(null);
          refreshInventory();
        }}
      />
      {deleteModalOpen && (
        <DeletePermission
          deleteFunction={() => deleteFunction(pendingDeleteIds)}
          onClose={handleCloseDeleteModal}
          onDeleteText={translations[langMode].aresurewanttodeleterecord}
        />
      )}
    </div>
  );
};
